<template>
  <article class="docs-article api-detail-article">
    <div v-if="loading" class="api-state">
      <span class="state-spinner" aria-hidden="true"></span>
      <strong>正在加载接口文档</strong>
      <p>正在从 Springdoc 获取最新的接口定义。</p>
    </div>

    <div v-else-if="error" class="api-state api-state-error">
      <strong>接口文档加载失败</strong>
      <p>{{ error }}</p>
      <button type="button" @click="$emit('retry')">重新加载</button>
    </div>

    <div v-else-if="!endpoint" class="api-state api-state-error">
      <strong>未找到该接口</strong>
      <p>接口可能已被移除，请从左侧接口列表重新选择。</p>
    </div>

    <template v-else>
      <div class="breadcrumb">
        <span>开放平台文档</span><i>/</i><span>接口列表</span><i>/</i><span>{{ endpoint.groupName }}</span><i>/</i>{{ endpoint.summary }}
      </div>

      <section class="api-title">
        <h1>{{ endpoint.summary }}</h1>
        <div class="api-endpoint-line">
          <span :class="['method', endpoint.method.toLowerCase()]">{{ endpoint.method }}</span>
          <code>{{ endpoint.path }}</code>
        </div>
      </section>

      <section class="api-doc-section">
        <h2>接口说明</h2>
        <div v-if="endpoint.description" class="markdown-body" v-html="descriptionHtml"></div>
        <div v-else class="document-placeholder">
          <span class="placeholder-mark" aria-hidden="true">文档</span>
          <p>该接口暂未配置详细说明。</p>
        </div>
      </section>

      <section class="api-doc-section">
        <h2>注意事项</h2>
        <div v-if="endpoint.notes" class="markdown-body notes-body" v-html="notesHtml"></div>
        <div v-else class="document-placeholder">
          <span class="placeholder-mark" aria-hidden="true">文档</span>
          <p>该接口暂无额外注意事项。</p>
        </div>
      </section>

      <section class="api-doc-section">
        <div class="api-section-heading">
          <h2>请求参数</h2>
        </div>
        <template v-if="requestParameterGroups.length">
          <div v-for="group in requestParameterGroups" :key="group.key" class="request-parameter-group">
            <div class="parameter-group-heading">
              <h3>{{ group.label }}</h3>
              <code v-if="group.mediaType">{{ group.mediaType }}</code>
            </div>
            <div class="parameter-table-wrap">
              <table class="parameter-table">
                <thead><tr><th>参数名</th><th>类型</th><th>必填</th><th>说明</th></tr></thead>
                <tbody>
                  <tr v-for="field in group.fields" :key="field.key">
                    <td><code>{{ field.name }}</code></td>
                    <td>{{ field.type }}</td>
                    <td><span :class="['required-mark', { required: field.required }]">{{ field.required ? '是' : '否' }}</span></td>
                    <td>{{ field.description || '-' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </template>
        <p v-else class="parameter-empty">该接口没有请求参数。</p>
      </section>

      <section class="api-doc-section">
        <div class="api-section-heading">
          <h2>响应参数</h2>
        </div>
        <div v-if="responseFields.length" class="parameter-table-wrap">
          <table class="parameter-table response-parameter-table">
            <thead><tr><th>参数名</th><th>类型</th><th>说明</th></tr></thead>
            <tbody>
              <tr v-for="field in responseFields" :key="field.key">
                <td><code>{{ field.name }}</code></td>
                <td>{{ field.type }}</td>
                <td>{{ field.description || '-' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p v-else class="parameter-empty">成功响应的 data 暂无可展示参数。</p>
      </section>

      <section class="api-doc-section">
        <h2>请求示例</h2>
        <div v-if="endpoint.requestExamples && endpoint.requestExamples.length" class="api-example-list">
          <div v-for="(example, index) in endpoint.requestExamples" :key="example.id || index" class="api-example-card">
            <div class="api-example-heading">
              <strong>{{ example.name || `请求示例 ${index + 1}` }}</strong>
              <code>{{ example.contentType || 'application/json' }}</code>
            </div>
            <pre><code>{{ formatExampleContent(example) }}</code></pre>
            <p v-if="example.description" class="api-example-description">{{ example.description }}</p>
          </div>
        </div>
        <div v-else class="document-placeholder">
          <span class="placeholder-mark" aria-hidden="true">文档</span>
          <p>该接口暂未配置请求示例。</p>
        </div>
      </section>

      <section class="api-doc-section">
        <h2>响应示例</h2>
        <div v-if="endpoint.responseExamples && endpoint.responseExamples.length" class="api-example-list">
          <div v-for="(example, index) in endpoint.responseExamples" :key="example.id || index" class="api-example-card">
            <div class="api-example-heading">
              <strong>{{ example.name || `响应示例 ${index + 1}` }}</strong>
              <code>{{ example.contentType || 'application/json' }}</code>
            </div>
            <pre><code>{{ formatExampleContent(example) }}</code></pre>
            <p v-if="example.description" class="api-example-description">{{ example.description }}</p>
          </div>
        </div>
        <div v-else class="document-placeholder">
          <span class="placeholder-mark" aria-hidden="true">文档</span>
          <p>该接口暂未配置响应示例。</p>
        </div>
      </section>
    </template>
  </article>
</template>

<script>
import { renderMarkdown } from '../../utils/markdown';
import { formatExampleContent } from '../../utils/openApiExample';

export default {
  name: 'OpenPlatformApiDetail',
  props: {
    endpoint: { type: Object, default: null },
    openApiDocument: { type: Object, default: null },
    loading: { type: Boolean, default: false },
    error: { type: String, default: '' }
  },
  computed: {
    descriptionHtml() {
      return renderMarkdown(this.endpoint && this.endpoint.description);
    },
    notesHtml() {
      return renderMarkdown(this.endpoint && this.endpoint.notes);
    },
    requestParameterGroups() {
      if (!this.endpoint || !this.endpoint.operation) return [];
      const operation = this.endpoint.operation;
      const groups = [];
      const groupsByLocation = {};
      (operation.parameters || []).forEach(parameter => {
        const location = this.locationLabel(parameter.in);
        let group = groupsByLocation[location];
        if (!group) {
          group = {
            key: location,
            label: this.locationTitle(parameter.in),
            mediaType: '',
            fields: []
          };
          groupsByLocation[location] = group;
          groups.push(group);
        }
        group.fields.push(this.toField(parameter.name, parameter.schema || {}, {
          location: location,
          required: Boolean(parameter.required),
          description: parameter.description || ''
        }));
      });

      const requestBody = operation.requestBody;
      const content = requestBody && requestBody.content;
      const mediaType = content && this.pickMediaTypeName(content);
      const media = content && content[mediaType];
      if (media && media.schema) {
        const fields = [];
        this.collectSchemaFields(media.schema, '', Boolean(requestBody.required), 'body', 0, fields);
        if (fields.length) {
          groups.push({
            key: 'body',
            label: 'Body 请求体',
            mediaType: mediaType,
            fields: fields
          });
        }
      }
      return groups;
    },
    successResponse() {
      if (!this.endpoint || !this.endpoint.operation || !this.endpoint.operation.responses) return null;
      const responses = this.endpoint.operation.responses;
      const status = Object.keys(responses).filter(code => /^2\d\d$/.test(code)).sort()[0];
      if (!status) return null;
      return { status: status, response: responses[status] };
    },
    responseFields() {
      if (!this.successResponse) return [];
      const media = this.pickMediaType(this.successResponse.response.content);
      if (!media || !media.schema) return [];
      const responseSchema = this.resolveSchema(media.schema);
      const dataSchema = responseSchema.properties && responseSchema.properties.data;
      if (!dataSchema) return [];

      const fields = [];
      const resolvedDataSchema = this.resolveSchema(dataSchema);
      if (resolvedDataSchema.properties) {
        this.collectSchemaFields(dataSchema, '', false, 'response', 0, fields);
      } else if (resolvedDataSchema.type === 'array' && resolvedDataSchema.items) {
        const itemSchema = this.resolveSchema(resolvedDataSchema.items);
        if (itemSchema.properties) {
          this.collectSchemaFields(resolvedDataSchema.items, '', false, 'response', 0, fields);
        } else {
          fields.push(this.toField('data[]', resolvedDataSchema.items, {
            location: 'response',
            required: false,
            description: itemSchema.description || ''
          }));
        }
      } else if (resolvedDataSchema.type && resolvedDataSchema.type !== 'object') {
        fields.push(this.toField('data', dataSchema, {
          location: 'response',
          required: false,
          description: resolvedDataSchema.description || '业务响应数据'
        }));
      }
      return fields;
    }
  },
  methods: {
    formatExampleContent(example) {
      return formatExampleContent(example && example.content, example && example.contentType);
    },
    pickMediaType(content) {
      if (!content) return null;
      return content['application/json'] || content['*/*'] || content[Object.keys(content)[0]] || null;
    },
    pickMediaTypeName(content) {
      if (!content) return '';
      if (content['application/json']) return 'application/json';
      if (content['*/*']) return '*/*';
      return Object.keys(content)[0] || '';
    },
    resolveSchema(schema) {
      if (!schema || !schema.$ref) return schema || {};
      const name = schema.$ref.split('/').pop();
      const schemas = this.openApiDocument && this.openApiDocument.components && this.openApiDocument.components.schemas;
      return schemas && schemas[name] ? schemas[name] : schema;
    },
    collectSchemaFields(schema, prefix, parentRequired, location, depth, target) {
      const resolved = this.resolveSchema(schema);
      const properties = resolved.properties || {};
      const requiredNames = resolved.required || [];

      if (!Object.keys(properties).length) {
        if (prefix) {
          target.push(this.toField(prefix, resolved, { location: location, required: parentRequired }));
        }
        return;
      }

      Object.keys(properties).forEach(name => {
        const propertySchema = properties[name];
        const fieldName = prefix ? prefix + '.' + name : name;
        const required = requiredNames.indexOf(name) !== -1;
        target.push(this.toField(fieldName, propertySchema, {
          location: location,
          required: required,
          description: this.resolveSchema(propertySchema).description || propertySchema.description || ''
        }));

        if (depth >= 2) return;
        const nested = this.resolveSchema(propertySchema);
        if (nested.properties) {
          this.collectSchemaFields(propertySchema, fieldName, required, location, depth + 1, target);
        } else if (nested.type === 'array' && nested.items && this.resolveSchema(nested.items).properties) {
          this.collectSchemaFields(nested.items, fieldName + '[]', required, location, depth + 1, target);
        }
      });
    },
    toField(name, schema, options) {
      const resolved = this.resolveSchema(schema);
      return {
        key: options.location + ':' + name,
        name: name,
        location: options.location,
        type: this.typeLabel(schema),
        required: Boolean(options.required),
        description: options.description || resolved.description || ''
      };
    },
    typeLabel(schema) {
      const resolved = this.resolveSchema(schema);
      let type = resolved.type || (resolved.properties ? 'object' : 'object');
      if (type === 'array') {
        type = 'array<' + this.typeLabel(resolved.items || {}) + '>';
      } else if (resolved.format && type !== 'string') {
        type += ' (' + resolved.format + ')';
      }
      return type;
    },
    locationLabel(location) {
      const labels = { path: 'path', query: 'query', header: 'header', cookie: 'cookie' };
      return labels[location] || location || 'parameter';
    },
    locationTitle(location) {
      const titles = {
        path: 'Path 路径参数',
        query: 'Query 查询参数',
        header: 'Header 请求头参数',
        cookie: 'Cookie 参数'
      };
      return titles[location] || '其他参数';
    }
  }
};
</script>

<style scoped>
.api-detail-article { max-width: 980px; }
.breadcrumb { display: flex; align-items: center; flex-wrap: wrap; gap: 9px; margin-bottom: 38px; color: #98a2b3; font-size: 12px; }
.breadcrumb span { color: #667085; }
.breadcrumb i { color: #d0d5dd; font-style: normal; }
.api-title { padding: 8px 0 34px; border-bottom: 1px solid #e7e9ee; }
.api-endpoint-line { display: flex; align-items: center; gap: 12px; }
.api-endpoint-line code { color: #475467; font-family: Consolas, "SFMono-Regular", monospace; font-size: 14px; }
.api-title h1 { margin: 0 0 18px; color: #172033; font-size: 34px; line-height: 1.25; letter-spacing: -.8px; }
.api-doc-section { padding-top: 48px; }
.api-doc-section h2 { margin: 0 0 18px; color: #1d2535; font-size: 22px; }
.api-section-heading { display: flex; align-items: center; justify-content: space-between; gap: 16px; }
.document-placeholder { display: flex; align-items: center; gap: 14px; min-height: 74px; padding: 16px 18px; color: #7a8292; border: 1px dashed #d9dde5; border-radius: 10px; background: #fafafa; }
.document-placeholder p { margin: 0; font-size: 12px; }
.placeholder-mark { padding: 5px 8px; color: #9a6108; border-radius: 5px; background: #fff0cc; font-size: 9px; font-weight: 800; letter-spacing: .5px; }
.request-parameter-group + .request-parameter-group { margin-top: 26px; }
.parameter-group-heading { display: flex; align-items: center; gap: 10px; margin: 0 0 11px; }
.parameter-group-heading h3 { margin: 0; color: #344054; font-size: 14px; }
.parameter-group-heading code { padding: 3px 7px; color: #667085; border-radius: 5px; background: #f2f4f7; font-family: Consolas, "SFMono-Regular", monospace; font-size: 10px; }
.parameter-table-wrap { overflow-x: auto; border: 1px solid #e7e9ee; border-radius: 11px; }
.parameter-table { width: 100%; min-width: 640px; border-collapse: collapse; background: #fff; }
.parameter-table th { padding: 12px 15px; color: #7c8493; background: #f8f9fa; font-size: 10px; text-align: left; }
.parameter-table td { padding: 14px 15px; color: #667085; border-top: 1px solid #eceef1; font-size: 11px; line-height: 1.6; vertical-align: top; }
.parameter-table td:first-child { color: #344054; }
.parameter-table code { color: #9a5b00; font-family: Consolas, "SFMono-Regular", monospace; font-size: 11px; }
.response-parameter-table th:first-child { width: 230px; }
.required-mark { color: #98a2b3; font-size: 9px; }
.required-mark.required { color: #b54708; font-weight: 700; }
.parameter-empty { margin: 0; padding: 18px; color: #8a92a1; border: 1px solid #e7e9ee; border-radius: 10px; background: #fafafa; font-size: 11px; }
.api-example-list { display: flex; flex-direction: column; gap: 16px; }
.api-example-card { border: 1px solid #e1e5eb; border-radius: 10px; background: #fff; overflow: hidden; }
.api-example-heading { display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 13px 16px; border-bottom: 1px solid #e7e9ee; background: #f8f9fa; }
.api-example-heading strong { color: #344054; font-size: 12px; }
.api-example-heading code { padding: 3px 7px; color: #9a6108; border-radius: 5px; background: #fff0cc; font-family: Consolas, "SFMono-Regular", monospace; font-size: 10px; }
.api-example-description { margin: 0; padding: 12px 16px; color: #667085; border-top: 1px solid #e7e9ee; background: #fff; font-size: 11px; line-height: 1.7; }
.api-example-card pre { margin: 0; padding: 16px; overflow: auto; color: #d4e2f4; background: #182230; font-family: Consolas, "SFMono-Regular", monospace; font-size: 11px; line-height: 1.7; white-space: pre-wrap; word-break: break-word; }
.markdown-body { padding: 18px 20px; color: #475467; border: 1px solid #e7e9ee; border-radius: 10px; background: #fafafa; font-size: 12px; line-height: 1.8; overflow-wrap: anywhere; }
.markdown-body.notes-body { border-color: #f0dfb9; background: #fffbf2; }
.markdown-body /deep/ p { margin: 0 0 12px; }
.markdown-body /deep/ p:last-child { margin-bottom: 0; }
.markdown-body /deep/ h1, .markdown-body /deep/ h2, .markdown-body /deep/ h3, .markdown-body /deep/ h4 { margin: 18px 0 9px; color: #27364b; }
.markdown-body /deep/ h1:first-child, .markdown-body /deep/ h2:first-child, .markdown-body /deep/ h3:first-child, .markdown-body /deep/ h4:first-child { margin-top: 0; }
.markdown-body /deep/ ul, .markdown-body /deep/ ol { margin: 8px 0; padding-left: 22px; }
.markdown-body /deep/ code { padding: 2px 5px; color: #9a6108; border-radius: 4px; background: #fff0cc; font-family: Consolas, "SFMono-Regular", monospace; }
.markdown-body /deep/ pre { padding: 14px; overflow: auto; border: 1px solid #263449; border-radius: 7px; background: #182230; }
.markdown-body /deep/ pre code { padding: 0; color: #d4e2f4; background: transparent; }
.markdown-body /deep/ blockquote { margin: 12px 0; padding: 9px 13px; color: #73510f; border-left: 3px solid #d99a26; background: #fff4d9; }
.markdown-body /deep/ blockquote p:last-child { margin-bottom: 0; }
.markdown-body /deep/ a { color: #b66a08; text-decoration: none; }
.markdown-body /deep/ a:hover { text-decoration: underline; }
.markdown-body /deep/ hr { margin: 18px 0; border: 0; border-top: 1px solid #e2e5ea; }
.markdown-body /deep/ table { width: 100%; margin: 14px 0; border-collapse: collapse; background: #fff; font-size: 11px; }
.markdown-body /deep/ th, .markdown-body /deep/ td { padding: 9px 11px; border: 1px solid #e1e5eb; text-align: left; vertical-align: top; }
.markdown-body /deep/ th { color: #344054; background: #f2f4f7; font-weight: 700; }
.markdown-body /deep/ tr:nth-child(even) { background: #fafafa; }
.markdown-body /deep/ img { display: block; max-width: 100%; height: auto; margin: 14px 0; border: 1px solid #e7e9ee; border-radius: 8px; }
.markdown-body /deep/ del { color: #8b93a1; }
.markdown-body /deep/ li + li { margin-top: 3px; }
.markdown-body /deep/ .task-list-item { list-style: none; }
.markdown-body /deep/ .task-list-item-checkbox { margin: 0 7px 0 -20px; accent-color: #d97706; }
.api-state { margin: 70px auto; padding: 42px; color: #667085; text-align: center; border: 1px solid #e7e9ee; border-radius: 13px; background: #fafafa; }
.api-state strong { display: block; color: #344054; font-size: 16px; }
.api-state p { margin: 10px 0 0; font-size: 12px; }
.api-state button { margin-top: 18px; padding: 8px 13px; color: #9a6108; border: 1px solid #f1d18f; border-radius: 7px; background: #fffaf0; cursor: pointer; }
.state-spinner { width: 22px; height: 22px; display: inline-block; margin-bottom: 15px; border: 2px solid #f4d28d; border-top-color: #d97706; border-radius: 50%; animation: api-spin .8s linear infinite; }
.method { display: inline-flex; align-items: center; justify-content: center; min-width: 58px; padding: 5px 9px; border-radius: 5px; font-size: 9px; font-weight: 800; letter-spacing: .45px; }
.method.post { color: #9a6108; background: #fff0cc; }
.method.put, .method.patch { color: #2765a4; background: #e8f2ff; }
.method.get { color: #18734e; background: #e5f6ef; }
.method.delete { color: #b13d46; background: #fdecee; }
@keyframes api-spin { to { transform: rotate(360deg); } }
@media (max-width: 760px) {
  .api-title h1 { font-size: 28px; }
  .api-doc-section { padding-top: 38px; }
  .api-endpoint-line { align-items: flex-start; flex-direction: column; }
  .api-endpoint-line code { word-break: break-all; }
}
</style>
